/**
 * Exportaciones del período — la parte "para archivar" de #47.
 *
 * `POST /api/v1/exports` encola un trabajo y devuelve 202: un CSV de un
 * millón de filas no cabe en un request. Acá se pide, se sigue el estado del
 * trabajo mientras corre, y se baja cuando está listo.
 *
 * **Qué se puede exportar y qué no.** La lista de `kind` es la de la API
 * (`alerts`, `batches`, `units`, `scans_daily`, `geo_cells`) y no incluye
 * eventos de escaneo. No es un olvido: es la regla 5 del dominio en el borde
 * por donde los datos salen de la plataforma — el tenant ve agregados, nunca
 * individuos. El CHECK de la migración repite la lista del lado de la base.
 *
 * Cada pedido y **cada descarga** quedan en `audit_log`. En farma esa
 * pregunta —quién se llevó qué, y cuándo— se hace años después.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { ApiError, apiErrorFromResponse, networkError } from '@/api/errors';
import { useApiQuery, useKeysetQuery } from '@/api/hooks';
import { exportKeys, type ReportPeriod } from '@/api/keys';
import type { components } from '@/api/schema';
import { useApi } from '@/api/useApi';
import type { MessageKey } from '@/i18n/messages';
import { useI18n } from '@/i18n/useI18n';
import { Badge, type Tone } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';

type ExportJob = components['schemas']['services__api__routers__exports__ExportOut'];
type ExportRequest = components['schemas']['ExportIn'];

const PAGE_SIZE = 10;

const KINDS = ['alerts', 'batches', 'units', 'scans_daily', 'geo_cells'] as const;
type Kind = (typeof KINDS)[number];

const KIND_KEYS: Record<Kind, MessageKey> = {
  alerts: 'reports.export.kind.alerts',
  batches: 'reports.export.kind.batches',
  units: 'reports.export.kind.units',
  scans_daily: 'reports.export.kind.scans_daily',
  geo_cells: 'reports.export.kind.geo_cells',
};

const FORMATS = ['csv', 'csv_excel'] as const;
type Format = (typeof FORMATS)[number];

const FORMAT_KEYS: Record<Format, MessageKey> = {
  csv: 'reports.export.format.csv',
  csv_excel: 'reports.export.format.csv_excel',
};

const STATUS_KEYS: Record<string, MessageKey> = {
  queued: 'reports.export.status.queued',
  running: 'reports.export.status.running',
  done: 'reports.export.status.done',
  failed: 'reports.export.status.failed',
  expired: 'reports.export.status.expired',
};

const STATUS_TONES: Record<string, Tone> = {
  queued: 'progress',
  running: 'progress',
  done: 'verified',
  failed: 'critical',
  expired: 'suspect',
};

export function ExportsPanel({ period }: { period: ReportPeriod }): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<Kind>('alerts');
  const [format, setFormat] = useState<Format>('csv');
  /** Trabajo recién pedido, mientras se genera. */
  const [tracking, setTracking] = useState<number | null>(null);

  // El trabajo recién pedido se sigue por su detalle. Cuando llega a un
  // estado terminal, `enabled` pasa a `false` y el polling se apaga solo: no
  // hace falta un efecto que limpie estado —y con él, un render en cascada—
  // porque la condición de corte se deriva de la propia respuesta.
  const tracked = useApiQuery<ExportJob>(
    exportKeys.detail(tracking ?? 0),
    `/api/v1/exports/${tracking ?? 0}`,
    undefined,
    { enabled: tracking !== null, refetchInterval: 2_000 },
  );

  const trackedStatus = tracked.data?.status;
  const working =
    tracking !== null && (trackedStatus === undefined || trackedStatus === 'queued' || trackedStatus === 'running');

  const jobs = useKeysetQuery<ExportJob>(exportKeys.list(PAGE_SIZE), '/api/v1/exports', undefined, {
    // La lista se refresca mientras el trabajo se genera, y deja de hacerlo
    // en cuanto termina.
    limit: PAGE_SIZE,
    ...(working ? { refetchInterval: 2_000 } : {}),
  });

  const request = useMutation<ExportJob, ApiError>({
    mutationFn: () => {
      const body: ExportRequest = {
        kind,
        format,
        // El período del reporte, en el mismo instante que la API interpreta
        // como comienzo del día.
        since: `${period.since}T00:00:00Z`,
      };
      return api.post<ExportJob>('/api/v1/exports', body);
    },
    onSuccess: (job) => {
      setTracking(job.id);
      void queryClient.invalidateQueries({ queryKey: exportKeys.all });
    },
  });

  const download = useMutation<unknown, ApiError, ExportJob>({
    mutationFn: (job) => downloadExport(api.currentSession()?.accessToken ?? null, job),
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    request.mutate();
  };

  const { query } = jobs;

  return (
    <Card className="print:hidden">
      <CardHeader title={t('reports.export.title')} description={t('reports.export.description')} />

      <CardBody className="border-b border-border">
        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-ink-muted">{t('reports.export.kind')}</span>
            <select
              value={kind}
              onChange={(event) => {
                setKind(event.target.value as Kind);
              }}
              className="mt-1 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
            >
              {KINDS.map((option) => (
                <option key={option} value={option}>
                  {t(KIND_KEYS[option])}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="block text-ink-muted">{t('reports.export.format')}</span>
            <select
              value={format}
              onChange={(event) => {
                setFormat(event.target.value as Format);
              }}
              className="mt-1 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
            >
              {FORMATS.map((option) => (
                <option key={option} value={option}>
                  {t(FORMAT_KEYS[option])}
                </option>
              ))}
            </select>
          </label>

          <Button type="submit" variant="primary" size="sm" disabled={request.isPending}>
            {request.isPending ? t('reports.export.requesting') : t('reports.export.request')}
          </Button>
        </form>

        {request.isError && <ErrorState className="mt-3" error={request.error} />}
        {download.isError && <ErrorState className="mt-3" error={download.error} />}
      </CardBody>

      {query.isPending && <LoadingState rows={3} />}

      {query.isError && (
        <CardBody>
          <ErrorState
            error={query.error}
            onRetry={() => {
              void query.refetch();
            }}
          />
        </CardBody>
      )}

      {!query.isPending && !query.isError && jobs.items.length === 0 && (
        <EmptyState title={t('reports.export.empty')} />
      )}

      {jobs.items.length > 0 && (
        <>
          <Table caption={t('reports.export.description')}>
            <thead>
              <tr>
                <Th>{t('reports.export.column.kind')}</Th>
                <Th>{t('reports.export.column.status')}</Th>
                <Th className="text-right">{t('reports.export.column.rows')}</Th>
                <Th>{t('reports.export.column.requestedBy')}</Th>
                <Th>{t('reports.export.column.created')}</Th>
                <Th>{t('reports.export.column.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {jobs.items.map((job) => (
                <tr key={job.id}>
                  <Td className="font-medium">{kindLabel(job.kind, t)}</Td>
                  <Td>
                    <Badge tone={STATUS_TONES[job.status] ?? 'neutral'}>{statusLabel(job.status, t)}</Badge>
                  </Td>
                  <Td className="text-right text-ink-muted">{fmt.number(job.row_count ?? null)}</Td>
                  {/* La firma de la exportación: quién se la llevó. */}
                  <Td className="text-ink-muted">{job.requested_email ?? '—'}</Td>
                  <Td className="text-ink-muted">{fmt.dateTime(job.created_at)}</Td>
                  <Td>
                    {job.status === 'done' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          download.mutate(job);
                        }}
                        disabled={download.isPending}
                      >
                        {download.isPending
                          ? t('reports.export.downloading')
                          : t('reports.export.download')}
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <CardBody className="flex items-center justify-center border-t border-border">
            {jobs.hasMore ? (
              <Button size="sm" onClick={jobs.loadMore} disabled={jobs.isLoadingMore}>
                {jobs.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
              </Button>
            ) : (
              <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
            )}
          </CardBody>
        </>
      )}
    </Card>
  );
}

function statusLabel(status: string, t: (key: MessageKey) => string): string {
  const key = STATUS_KEYS[status];
  return key === undefined ? status : t(key);
}

/** Un `kind` que la API agregue después se muestra como viene, sin romper. */
function kindLabel(kind: string, t: (key: MessageKey) => string): string {
  return (KINDS as readonly string[]).includes(kind) ? t(KIND_KEYS[kind as Kind]) : kind;
}

/**
 * La descarga no puede pasar por `ApiClient`: el cliente parsea JSON y esto
 * es un CSV (servido comprimido). Se hace con `fetch` y el access token que
 * el cliente ya tiene — no se guarda ni se copia a ningún lado.
 *
 * Lo prolijo sería que la API devolviera una URL firmada de vida corta, como
 * ya hace `GET /label-orders/{id}/print-file`; quedó anotado como pedido en
 * `docs/adr-inbox/b6-admin.md`.
 */
async function downloadExport(accessToken: string | null, job: ExportJob): Promise<void> {
  if (accessToken === null) {
    throw new ApiError({ status: 401, code: 'no_session', message: 'No hay sesión iniciada.' });
  }

  let response: Response;
  try {
    response = await fetch(`/api/v1/exports/${job.id}/download`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'omit',
      redirect: 'error',
    });
  } catch (cause) {
    throw networkError(cause);
  }
  if (!response.ok) throw await apiErrorFromResponse(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `xyvona-${job.kind}-${job.id}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}
